import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import type { FeedbackStatus } from '@/types';

interface FeedbackButtonsProps {
  messageId: string;
  feedback?: FeedbackStatus;
  onFeedback: (messageId: string, type: 'like' | 'dislike') => Promise<void>;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
}

export function FeedbackButtons({ 
  messageId, 
  feedback, 
  onFeedback, 
  className,
  size = 'sm' 
}: FeedbackButtonsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMobile, isSmallMobile } = useBreakpoint();

  const handleFeedback = async (type: 'like' | 'dislike') => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onFeedback(messageId, type);
    } catch (error) {
      console.error('Feedback submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLiked = feedback?.type === 'like';
  const isDisliked = feedback?.type === 'dislike';

  return (
    <div className={cn('flex items-center', isMobile ? 'gap-0.5' : 'gap-1', className)}>
      <Button
        variant="ghost"
        size={size}
        className={cn(
          'transition-colors',
          isMobile ? 'h-5 px-1.5 text-xs' : 'h-6 px-2 text-xs',
          isLiked && 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800',
          isSubmitting && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => handleFeedback('like')}
        disabled={isSubmitting}
        title={isLiked ? 'Remove like' : 'Like this response'}
      >
        {isSubmitting && feedback?.type !== 'like' ? (
          <Loader2 className={cn('animate-spin', isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
        ) : (
          <ThumbsUp className={cn(
            isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3',
            isLiked && 'fill-current'
          )} />
        )}
      </Button>
      
      <Button
        variant="ghost"
        size={size}
        className={cn(
          'transition-colors',
          isMobile ? 'h-5 px-1.5 text-xs' : 'h-6 px-2 text-xs',
          isDisliked && 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800',
          isSubmitting && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => handleFeedback('dislike')}
        disabled={isSubmitting}
        title={isDisliked ? 'Remove dislike' : 'Dislike this response'}
      >
        {isSubmitting && feedback?.type !== 'dislike' ? (
          <Loader2 className={cn('animate-spin', isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
        ) : (
          <ThumbsDown className={cn(
            isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3',
            isDisliked && 'fill-current'
          )} />
        )}
      </Button>

      {/* Show feedback timestamp if available - hide on small mobile */}
      {feedback?.type && feedback.timestamp && !isSmallMobile && (
        <span className="text-xs text-muted-foreground ml-1">
          {feedback.timestamp.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </span>
      )}
    </div>
  );
}